<template>
  <div>
    <b-card title="Webhook Setting">
      <b-row>
        <b-col cols="12">
          <b-card-text>
            Select this option if you need to enable webhooks
            Keep unselected if you need to disable webhooks
            <span style="color: red">*</span>
          </b-card-text>
        </b-col>
        <!-- Radio Selection -->
        <b-col cols="6">
          <b-form-radio-group
            v-model="webhookEnaSelected"
            v-can="'edit-webhook'"
            :options="webhookEnaOptions"
            class="demo-inline-spacing mb-1"
            value-field="value"
            text-field="text"
            disabled-field="disabled"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-card-actions
      v-if="webhookEnaSelected"
      title="Filters"
      action-collapse
      collapsed
    >
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Created Date"
            label-for="webhook_created_at"
          >
            <AdvancedDateFilter
              :date-value="filters['created_at']"
              :opens="'right'"
              @update="updateDateRange('created_at', $event)"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Name"
            label-for="webhook_name"
          >
            <b-form-input
              id="webhook_name"
              v-model="filters['name']"
              placeholder="Filter by webhook name"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Event Type"
            label-for="webhook_event_type"
          >
            <v-select
              v-model="filters['event_type']"
              :options="webhookEventTypeOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Select Event Type"
              clearable
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Status"
            label-for="webhook_is_active"
          >
            <v-select
              v-model="filters['is_active']"
              :options="webhookStatusOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Select Status"
              clearable
            />
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyWebhookFilters"
            :clear-filters="clearWebhookFilters"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card
      v-if="webhookEnaSelected"
      title="Webhooks Configuration"
      no-body
    >

      <div>
        <div class="m-2">
          <b-row class="d-sm-flex align-items-start">
            <b-button
              v-can="'create-webhook'"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              class="ml-1 custom-button-color mb-2 mb-md-0 mobile-width-fix mr-1 mr-md-0"
              @click="openAddWebhookModal"
            >
              <feather-icon icon="PlusIcon" />
              Add Webhook
            </b-button>
            <b-col
              class="d-flex d-sm-flex flex-wrap justify-content-end align-items-xl-center mb-1 mb-md-0"
            >
              <TableButtons
                :items="webhookRows"
                :json_fieldz="webhookJsonFields"
                :name="`Webhooks Configuration - Page ${webhookPage}.xls`"
                :bulkname="`Webhooks Configuration.xls`"
                :fetch="getWebhooksNoPagination"
                @refresh="onClickWebhookRefresh"
              />
            </b-col>
          </b-row>
        </div>

        <!-- table -->
        <b-overlay
          id="webhook-overlay"
          variant="light"
          opacity="0.30"
          blur="10px"
          rounded="sm"
        >
          <b-table
            id="webhook-config-table"
            striped
            hover
            responsive
            :per-page="webhookPerPage"
            :items="webhookRows"
            :fields="webhookFields"
            show-empty
            @sort-changed="webhookSortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="webhookRows"
                :data-loading="webhookLoading"
              />
            </template>

            <template #cell(name)="data">
              <div class="font-weight-bolder">
                {{ data.value }}
              </div>
            </template>

            <template #cell(url)="data">
              <div
                class="text-truncate"
                style="max-width: 200px;"
                :title="data.value"
              >
                {{ data.value }}
              </div>
            </template>

            <template #cell(request_type)="data">
              <b-badge variant="info">
                {{ data.value }}
              </b-badge>
            </template>

            <template #cell(event_type)="data">
              {{ data.value }}
            </template>

            <template #cell(is_active)="data">
              <b-badge :variant="data.value ? 'light-success' : 'light-danger'">
                {{ data.value ? 'Active' : 'Inactive' }}
              </b-badge>
            </template>

            <template #cell(max_retries)="data">
              <span class="font-weight-bold">{{ data.value }}</span>
            </template>

            <template #cell(field_mappings)="data">
              <b-button
                v-b-modal="`field-mappings-modal-${data.item.id}`"
                variant="outline-primary"
                size="sm"
              >
                View Mappings
              </b-button>
            </template>

            <template #cell(headers)="data">
              <b-button
                v-b-modal="`headers-modal-${data.item.id}`"
                variant="outline-secondary"
                size="sm"
              >
                View Headers
              </b-button>
            </template>

            <template #cell(created_at)="data">
              <div>
                {{ __dateTimeFormatter(data.value) }}
              </div>
            </template>

            <template #cell(action)="data">
              <b-dropdown
                v-can="'edit-webhook'"
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  v-can="'edit-webhook'"
                  @click="openEditWebhookModal(data.item)"
                >
                  <feather-icon icon="EditIcon" />
                  Edit
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row cols="12">
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="webhookPerPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="webhookPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">Showing {{ webhookMeta.from
                }} to {{
                  webhookMeta.to }} of {{ webhookMeta.total }} entries</span>
                <b-pagination
                  v-model="webhookPage"
                  :per-page="webhookPerPage"
                  :total-rows="(webhookMeta.total) ? webhookMeta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </div>
    </b-card>

    <!-- Field Mappings Modals -->
    <b-modal
      v-for="item in webhookRows"
      :id="`field-mappings-modal-${item.id}`"
      :key="`field-mappings-${item.id}`"
      :title="`Field Mappings - ${item.name}`"
      size="lg"
      scrollable
    >
      <div class="mb-3">
        <h6>Field Mappings:</h6>
        <div v-if="item.field_mappings && item.field_mappings.length > 0">
          <div
            v-for="(mapping, index) in item.field_mappings"
            :key="index"
            class="border rounded p-2 mb-2"
          >
            <div class="row">
              <div class="col-6">
                <strong>External Field:</strong>
                <code>{{ mapping.external_field }}</code>
              </div>
              <div class="col-6">
                <strong>Internal Field:</strong>
                <code>{{ mapping.internal_field }}</code>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-muted"
        >
          No field mappings configured
        </div>
      </div>
    </b-modal>

    <!-- Headers Modals -->
    <b-modal
      v-for="item in webhookRows"
      :id="`headers-modal-${item.id}`"
      :key="`headers-${item.id}`"
      :title="`Headers - ${item.name}`"
      size="lg"
      scrollable
    >
      <div class="mb-3">
        <h6>Headers:</h6>
        <div v-if="item.headers && item.headers.length > 0">
          <div
            v-for="(header, index) in item.headers"
            :key="index"
            class="border rounded p-2 mb-2"
          >
            <div class="row">
              <div class="col-6">
                <strong>Key:</strong>
                <code>{{ header.key }}</code>
              </div>
              <div class="col-6">
                <strong>Value:</strong>
                <code>{{ header.value }}</code>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-muted"
        >
          No headers configured
        </div>
      </div>
    </b-modal>

    <!-- Webhook Modals -->
    <create-webhook-modal ref="addWebhookModal" />
    <edit-webhook-modal ref="editWebhookModal" />
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BCard,
  BCardText,
  BFormRadioGroup,
  BBadge,
  BOverlay,
  BTable,
  BPagination,
  BDropdown,
  BDropdownItem,
  BModal,
  VBModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import TableButtons from '@/components/TableButtons/TableButtons.vue'
import TableDataFetching from '@/components/TableDataFetching.vue'
import CreateWebhookModal from '@/components/webhooks/AddWebhookModal.vue'
import EditWebhookModal from '@/components/webhooks/EditWebhookModal.vue'

const webhookLogsRepository = RepositoryFactory.get('webhookLogs')
export default {
  directives: {
    Ripple,
    'b-modal': VBModal,
  },
  components: {
    BFormRadioGroup,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BCard,
    BCardText,
    BBadge,
    BOverlay,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    BModal,
    vSelect,
    AdvancedDateFilter,
    FilterButtons,
    BCardActions,
    TableButtons,
    TableDataFetching,
    CreateWebhookModal,
    EditWebhookModal,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return {
      webhookEnaSelected: false,
      webhookEnaOptions: [
        { text: 'Enabled', value: true, disabled: false },
        { text: 'Disabled', value: false, disabled: false },
      ],
      webhookRows: [],
      originalWebhookRows: [],
      webhookLoading: false,
      webhookPerPage: 10,
      webhookPageOptions: [10, 20, 50],
      webhookPage: 1,
      webhookMeta: {},
      webhookSort: '',
      isInitialLoad: true, // Flag to prevent saving during initial load
      webhookFields: [
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'url',
          label: 'URL',
          sortable: false,
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'request_type',
          label: 'Method',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'event_type',
          label: 'Event Type',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'is_active',
          label: 'Status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'max_retries',
          label: 'Max Retries',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'field_mappings',
          label: 'Field Mappings',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'headers',
          label: 'Headers',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'created_at',
          label: 'Created Date',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'action',
          label: 'Action',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],
      webhookJsonFields: {
        Name: 'name',
        URL: 'url',
        Method: 'request_type',
        'Event Type': 'event_type',
        Status: 'is_active',
        'Max Retries': 'max_retries',
        'Created Date': {
          field: 'created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
      },
      // Webhook Filters
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
        name: '',
        event_type: '',
        is_active: '',
      },
      webhookEventTypeOptions: [],
      webhookStatusOptions: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
    }
  },
  watch: {
    webhookEnaSelected(newValue, oldValue) {
      // Only save if the value actually changed and it's not the initial load
      if (!this.isInitialLoad && oldValue !== undefined && newValue !== oldValue) {
        this.saveWebhookSetting()
      }
      if (this.webhookEnaSelected) {
        this.fetchWebhooks()
      } else {
        this.webhookRows = []
      }
    },
  },
  async mounted() {
    await this.fetchWebhookConfig()
    await this.fetchWebhookSettings()
    if (this.webhookEnaSelected) {
      await this.fetchWebhooks()
    }
    // Mark initial load as complete
    this.isInitialLoad = false
  },
  methods: {
    async fetchWebhookSettings() {
      try {
        const ResourceRepository = RepositoryFactory.get('businessSettings')
        const { data } = (await ResourceRepository.getMerchantSettigs()).data
        this.webhookEnaSelected = data.is_webhook_enable
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async saveWebhookSetting() {
      try {
        const ResourceRepository = RepositoryFactory.get('businessSettings')
        // First, get the current merchant settings to preserve existing data
        const currentSettings = await ResourceRepository.getMerchantSettigs()
        const currentData = currentSettings.data.data
        // Build the complete setting payload with existing data
        const setting = {
          name: currentData.merchant.name,
          description: currentData.merchant.description,
          is_active: currentData.merchant.is_active,
          business_setting: {
            is_waybill_auto: currentData.is_waybill_auto,
            is_pickup_enable: currentData.is_pickup_enable,
            is_webhook_enable: this.webhookEnaSelected,
          },
        }
        const res = await ResourceRepository.setMerchantSettigs(setting)
        if (res.status === 200) {
          this.showSuccessMessage('Webhook setting updated successfully')
          localStorage.removeItem('merchant_business_setting')
        } else {
          this.showErrorMessage('Cannot update webhook setting')
        }
      } catch (e) {
        this.showErrorMessage('Failed to save webhook setting. Please try again.')
      }
    },
    async fetchWebhookConfig() {
      try {
        const { data } = await webhookLogsRepository.getWebhookConfig()
        // Convert event_types to event type options
        this.webhookEventTypeOptions = Object.entries(data.data.event_types).map(([key, value]) => ({
          text: value,
          value: key,
        }))
      } catch (e) {
        // Fallback to default options if API fails
        this.webhookEventTypeOptions = [
          { text: 'Order Status Update', value: 'order_status_update' },
        ]
      }
    },
    async fetchWebhooks() {
      this.webhookLoading = true
      this.webhookRows = [] // Clear rows to show loading state
      try {
        // Build filter query excluding date range (webhook API might not support it)
        const filterQuery = this.buildFilterQueryExcludingDate()
        const { data } = await webhookLogsRepository.getWebhooksConfiguration(filterQuery)
        this.originalWebhookRows = data.data || []
        // Apply client-side filtering including date range
        this.webhookRows = this.applyClientSideFilters([...this.originalWebhookRows])
        // Set pagination meta
        this.webhookMeta = {
          from: 1,
          to: this.webhookRows.length,
          total: this.webhookRows.length,
        }
      } catch (e) {
        this.webhookRows = []
        this.originalWebhookRows = []
        this.webhookMeta = { from: 0, to: 0, total: 0 }
      }
      // Ensure loading state is visible for at least 500ms
      await new Promise(resolve => setTimeout(resolve, 500))
      this.webhookLoading = false
    },
    // Helper methods for client-side filtering
    buildFilterQueryExcludingDate() {
      // Build filter query without date range
      const queryParams = []
      if (this.filters.name) {
        queryParams.push(`filter[name]=${encodeURIComponent(this.filters.name)}`)
      }
      if (this.filters.event_type) {
        queryParams.push(`filter[event_type]=${encodeURIComponent(this.filters.event_type)}`)
      }
      if (this.filters.is_active !== '') {
        queryParams.push(`filter[is_active]=${this.filters.is_active}`)
      }
      return queryParams.join('&')
    },
    applyClientSideFilters(rows) {
      let filteredRows = [...rows]

      // Apply name filter
      if (this.filters.name) {
        const nameQuery = this.filters.name.toLowerCase()
        filteredRows = filteredRows.filter(webhook => (webhook.name || '').toLowerCase().includes(nameQuery))
      }

      // Apply event type filter
      if (this.filters.event_type) {
        filteredRows = filteredRows.filter(webhook => webhook.event_type === this.filters.event_type)
      }

      // Apply status filter
      if (this.filters.is_active !== '') {
        const isActiveFilter = typeof this.filters.is_active === 'string'
          ? this.filters.is_active === 'true'
          : !!this.filters.is_active
        filteredRows = filteredRows.filter(webhook => !!webhook.is_active === isActiveFilter)
      }

      // Apply date range filter
      if (this.filters.created_at.startDate && this.filters.created_at.endDate) {
        const startDate = new Date(this.filters.created_at.startDate)
        const endDate = new Date(this.filters.created_at.endDate)
        filteredRows = filteredRows.filter(webhook => {
          const webhookDate = new Date(webhook.created_at)
          return webhookDate >= startDate && webhookDate <= endDate
        })
      }
      return filteredRows
    },
    // Webhook Filter Methods
    applyWebhookFilters() {
      this.fetchWebhooks()
    },
    clearWebhookFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
        name: '',
        event_type: '',
        is_active: '',
      }
      this.$nextTick(() => {
        this.fetchWebhooks()
      })
    },
    // Webhook Table Methods
    webhookSortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.webhookSort = active + key
        this.fetchWebhooks()
      }
    },
    onClickWebhookRefresh() {
      this.fetchWebhooks()
    },
    async getWebhooksNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = await webhookLogsRepository.getWebhooksConfiguration(filterQuery)
        return data.data || []
      } catch (e) {
        return []
      }
    },
    // Webhook Form Methods
    openAddWebhookModal() {
      this.$refs.addWebhookModal.openModal()
    },
    openEditWebhookModal(webhook) {
      this.$refs.editWebhookModal.openModal(webhook)
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../../@core/scss/button-color.scss';
@import '../@core/scss/vue/libs/vue-select.scss';
@media (max-width: 767px) {
    #webhook-config-table thead th {
        position: sticky;
        top: 0;
    }
}
</style>
